import {observer} from 'mobx-react-lite'
import React, {useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {newsCompanyId} from 'yougile-model'
import {Items} from '@api/model/items'
import {Sync} from '@client/sync'
import {InteractiveIcon as Icon} from '@components/common/interactive-icon/interactive-icon'
import {useGlobalKeyDown} from '@containers/hooks'
import PencilIcon from './icons/icon-intask-edit.svg'
import {useTaskDescription} from './task-description-store'

const MainContainer = tw.div`
  z-1000
  top-96
  w-full
  pt-32
  h-72
  min-h-72
`

const Container = tw.div<{$hover: boolean}>`
  flex
  max-w-960
  mx-auto
  relative
  ${p => (p.$hover ? 'bg-main-even-old' : '')}
`

const TextContainer = tw.div`
  pl-24
  h-40
  leading-10
  text-26
  text-main-old
  font-semibold
  overflow-hidden
  whitespace-nowrap
  overflow-ellipsis

  border-1
  border-transparent
  border-solid
  box-border
  invisible
`

const Input = tw.input<{$editMode: boolean}>`
  absolute
  px-24
  h-40
  text-26
  text-main-old
  font-bold
  outline-none
  leading-10

  shadow-none
  focus:shadow-none
  hover:shadow-none
  all-unset
  overflow-hidden
  cursor-text
  bg-transparent
  w-full
  box-border
  focus:bg-transparent
  hover:bg-transparent

  focus:border-1
  focus:border-solid

  border-1
  border-solid

  ${p =>
    p.$editMode
      ? 'border-main-old focus:border-main-old'
      : `border-transparent focus:border-transparent whitespace-nowrap overflow-ellipsis`}
`

export const TaskTitleInput = observer(() => {
  const [, store] = useTaskDescription()
  const [title, setTitle] = useState(store?.taskHub?.title ?? '')
  const [hover, setHover] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const inputRef = useRef<HTMLInputElement>()

  useGlobalKeyDown({escape: store.onEscape})

  const setEditModeOff = () => {
    setEditMode(false)
    inputRef?.current?.blur()
  }

  const onCancel = () => {
    setTitle(store?.taskHub?.title ?? '')
    setEditModeOff()
  }

  const onCommit = () => {
    const task = store?.taskHub
    if (!task) {
      return
    }

    const trimmedTitle = title.trim()

    if (trimmedTitle === '') {
      onCancel()
      return
    }

    if (task.title !== trimmedTitle) {
      const box = Sync.get(task)
      const canEdit =
        box.companyId === newsCompanyId ||
        Items.rename(Items.fromHub(task), trimmedTitle)
      if (canEdit) {
        box.editTitle(task, trimmedTitle)
      }
    }

    setEditModeOff()
  }

  const onEditModeOnByIcon = () => {
    setEditMode(true)
    setTimeout(() => {
      inputRef?.current?.focus()
      inputRef?.current?.setSelectionRange(0, title.length)
    }, 100)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = (e.key || '').toLowerCase()
    key === 'enter' && onCommit()
    if (key === 'escape') {
      onCancel()
      e.stopPropagation()
    }
  }

  return (
    <MainContainer>
      <Container
        $hover={hover}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <Input
          className="task-title-input"
          $editMode={editMode}
          ref={inputRef}
          type="text"
          value={title}
          onChange={value => setTitle(value?.target?.value ?? '')}
          onClick={() => setEditMode(true)}
          onBlur={onCommit}
          onKeyDown={onKeyDown}
          placeholder={Lang.msg.editTaskNamePlaceholder}
        />
        {!editMode && (
          <TextContainer>{title || store?.taskHub?.title}</TextContainer>
        )}
        {!editMode && hover && (
          <Icon onClick={onEditModeOnByIcon} className="z-1000">
            <PencilIcon />
          </Icon>
        )}
      </Container>
    </MainContainer>
  )
})
